import React, { FC, useEffect } from 'react';
import { DataTable, parseQuery } from '@jjj/components';
import { connect } from 'react-redux';
import { withRouter, WithRouterProps } from 'src/containers/withRouter/withRouter';
import { RootState } from 'src/redux/store';
import { tableConfig } from 'src/settings';
import startCase from 'lodash/startCase';
import { IParams } from '@dash/table';
import { IAction } from '@dash/actions';
import { IMethod } from '@dash/apiInstance';
import { OwnProps } from './agencyInvites.types';
import { filtersConfig } from '../../poolList';
import actions from './actions';
import { t } from '../../../../../../../../i18n';
import { IAgency, ILoadDataRequestPayload, IProcessRequestPayload } from './types';
import { getAgenciesColumns, getAgenciesToInviteColumns } from './columns';
import { Header } from './agencyInvites.style';

const AgencyInvites: FC<OwnProps & WithRouterProps> = ({
  isTabOpen,
  data,
  history,
  loadAgencies,
  loadAllAvailableAgencies,
  bookingId,
  processActionRequest,
  clearStore,
  isRequestSpecificAgencyEnabled,
  agencyMarkupVisibilityEnabled,
}) => {
  const {
    agencies,
    isAgenciesLoading,
    agenciesFilteredTotal,
    allAvailableAgenciesFilteredTotal,
    allAvailableAgencies,
    isAllAvailableAgenciesLoading,
  } = data;
  const { search = '' } = history.location || {};
  const query = parseQuery(search);

  useEffect(() => {
    if (!isAgenciesLoading && typeof agencies === 'undefined') {
      loadAgencies({ id: bookingId });
    }
  }, [isAgenciesLoading, agencies, loadAgencies, bookingId]);

  useEffect(() => {
    if (
      isRequestSpecificAgencyEnabled &&
      !isAllAvailableAgenciesLoading &&
      typeof allAvailableAgencies === 'undefined'
    ) {
      loadAllAvailableAgencies({ id: bookingId, pageSize: tableConfig.virtualTablePageSize });
    }
  }, [
    isAllAvailableAgenciesLoading,
    allAvailableAgencies,
    loadAllAvailableAgencies,
    bookingId,
    isRequestSpecificAgencyEnabled,
  ]);

  useEffect(() => {
    if (isRequestSpecificAgencyEnabled && isTabOpen && !isAgenciesLoading && typeof agencies !== 'undefined') {
      loadAgencies({ id: bookingId });
    }
  }, [isTabOpen]);

  useEffect(() => {
    if (isTabOpen && !isAllAvailableAgenciesLoading && typeof allAvailableAgencies !== 'undefined') {
      loadAllAvailableAgencies({ id: bookingId, pageSize: tableConfig.virtualTablePageSize });
    }
  }, [isTabOpen]);

  useEffect(() => () => clearStore(), []);

  const loadAgenciesData = (params: IParams) => {
    const { order, ...rest } = params;
    loadAgencies({
      id: bookingId,
      ...rest,
      order: typeof order === 'string' ? order : undefined,
    });
  };

  const loadAllAvailableAgenciesData = (params: IParams) => {
    const { order, ...rest } = params;
    loadAllAvailableAgencies({
      id: bookingId,
      ...rest,
      order: typeof order === 'string' ? order : undefined,
    });
  };

  const processAction = (agency: IAgency, action: IAction) => {
    processActionRequest({
      url: action.url,
      method: action.method?.toUpperCase() as IMethod,
      bookingId,
      poolId: agency.id,
      key: action.key,
    });
  };

  const popoverContent = (agency: IAgency) => {
    return agency.actions.map((action: IAction) => {
      const actionKey = action?.key || action?.name?.toLowerCase().replace(' ', '_');
      const actionFallbackName = action?.name || startCase(action.key.replace(/_/g, ' '));

      return {
        ...action,
        name: t(`dash.jobs.bookings.poolList.actions.${actionKey}`, {}, actionFallbackName),
        onActionClick: () => processAction(agency, action),
      };
    });
  };

  return (
    <>
      <DataTable
        columns={getAgenciesColumns(popoverContent, agencyMarkupVisibilityEnabled)}
        dataSource={agencies}
        loadData={loadAgenciesData}
        loading={isAgenciesLoading}
        onChange={loadAgenciesData}
        filter={query.filter}
        total={agenciesFilteredTotal}
        filtersConfig={filtersConfig}
        titleContentLeft={<Header>{t('dash.jobs.bookings.pools.agency_invites_tab.agencies_invited_header')}</Header>}
      />

      {isRequestSpecificAgencyEnabled && (
        <DataTable
          columns={getAgenciesToInviteColumns(popoverContent, agencyMarkupVisibilityEnabled)}
          dataSource={allAvailableAgencies}
          loadData={loadAllAvailableAgenciesData}
          loading={isAllAvailableAgenciesLoading}
          onChange={loadAllAvailableAgenciesData}
          total={allAvailableAgenciesFilteredTotal}
          filtersConfig={filtersConfig}
          hideSearchbar
          titleContentLeft={
            <Header>{t('dash.jobs.bookings.pools.agency_invites_tab.all_available_agencies_header')}</Header>
          }
        />
      )}
    </>
  );
};

export default connect(
  (state: RootState) => ({
    data: state.PoolAgencies,
    isRequestSpecificAgencyEnabled: state.StartAppSettings?.feature_flags?.request_specific_agency_enabled,
    agencyMarkupVisibilityEnabled: state.StartAppSettings?.feature_flags?.agency_markup_visibility_enabled,
  }),
  dispatch => ({
    loadAgencies: /* istanbul ignore next */ (data: ILoadDataRequestPayload<{}, {}>) =>
      dispatch(actions.loadAgenciesRequest(data)),
    processActionRequest: /* istanbul ignore next */ (data: IProcessRequestPayload<{}, {}>) =>
      dispatch(actions.processActionRequest(data)),
    loadAllAvailableAgencies: /* istanbul ignore next */ (data: ILoadDataRequestPayload<{}, {}>) =>
      dispatch(actions.loadAllAvailableAgenciesRequest(data)),
    clearStore: /* istanbul ignore next */ () => dispatch(actions.clearStore()),
  }),
)(withRouter(AgencyInvites));
